import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, TaxApi } from "../../../../../../../../../../services/Api/Organization/types";
import InvoicePreview from "../../components/InvoicePreview/InvoicePreview";

interface MatchParams {
	invoiceId: string;
}

const OrganizationDocumentInvoicePreviewPage = () => {
	const { addFlash } = useFlash();
	const { invoiceId } = useParams<MatchParams>();
	const [invoice, setInvoice] = useState<InvoiceApi | undefined>();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();
	const [taxes, setTaxes] = useState<TaxApi[] | undefined>();

	useEffect(() => {
		init();
	}, []);

	const init = async () => {
		await fetchInvoiceInfo();
		await fetchTaxes();
	};

	const fetchInvoiceInfo = async () => {
		try {
			const invoiceParams = {
				include: "all",
			};
			const customFieldsParams = { f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=INVOICE`) };
			const requests: [Promise<InvoiceApi>, Promise<CustomFieldTemplateApi[]>] = [
				apiOrganization.getInvoice(Number(invoiceId), invoiceParams),
				apiOrganization.getCustomFields(customFieldsParams),
			];
			const [invoiceResponse, customFieldsResponse] = await Promise.all(requests);
			setInvoice(invoiceResponse);
			setCustomFieldsConfig(customFieldsResponse);
			const invoiceNumber =
				invoiceResponse.status === "DRAFT"
					? `${t("enums.invoices.document_type.PROFORMA")} #${invoiceResponse.id}`
					: `${t("enums.invoices.document_type.INVOICE")} #${invoiceResponse.document_number.full_number}`;
			handleChangeTabTitle(`${invoiceNumber} | ${t("modules.invoice.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchInvoice = async () => {
		try {
			const params = {
				include: "all",
			};
			const res = await apiOrganization.getInvoice(Number(invoiceId), params);
			setInvoice(res);
			const invoiceNumber =
				res.status === "DRAFT"
					? `${t("enums.invoices.document_type.PROFORMA")} #${res.id}`
					: `${t("enums.invoices.document_type.INVOICE")} #${res.document_number.full_number}`;
			handleChangeTabTitle(`${invoiceNumber} | ${t("modules.invoice.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchTaxes = async () => {
		try {
			const res = await apiOrganization.getTaxes({ size: 0 });
			setTaxes(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!invoice || !customFieldsConfig || !taxes) return <LoadingContainer />;

	return (
		<InvoicePreview
			invoice={invoice}
			customFieldsConfig={customFieldsConfig}
			setInvoice={setInvoice}
			fetchInvoice={fetchInvoice}
			taxes={taxes}
		/>
	);
};

export default OrganizationDocumentInvoicePreviewPage;
